/**
 * ProfileMyDataHub — „Моите данни" в профила.
 * Един вход за всички данни на пациента: качване на документ, състояние на
 * лабораториите, автодиагнозата, албумина, заболяванията, лекарствата.
 * Данни: GET /api/my-data/status
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  Database, ChevronRight, FileText, CheckCircle2, Circle, AlertCircle, Loader2,
  FlaskConical, Stethoscope, Activity, Droplets, ClipboardList, Pill, Heart,
  Ruler, Users, Dna, CalendarCheck, Calculator, Baby, ScanLine, Network,
} from "lucide-react";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";
import UnifiedDocumentUpload from "@/components/breast_intel/UnifiedDocumentUpload";
import FamilyHistoryModal from "@/components/profile/FamilyHistoryModal";
import RiskScoreModal from "@/components/breast_intel/RiskScoreModal";
import ReproductiveHistoryModal from "@/components/profile/ReproductiveHistoryModal";

const ICONS = {
  flask: FlaskConical, stethoscope: Stethoscope, activity: Activity,
  droplets: Droplets, clipboard: ClipboardList, pill: Pill, heart: Heart,
  ruler: Ruler, users: Users, dna: Dna, file: FileText, calendar: CalendarCheck,
  calculator: Calculator, baby: Baby, scan: ScanLine, network: Network,
};

const PILL = {
  complete: { Icon: CheckCircle2, cls: "text-emerald-500", label: "Добавени" },
  partial: { Icon: AlertCircle, cls: "text-amber-500", label: "Частично" },
  missing: { Icon: Circle, cls: "text-slate-400", label: "Липсват" },
};

function StatusPill({ status }) {
  const cfg = PILL[status] || PILL.missing;
  const { Icon } = cfg;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-bold ${cfg.cls}`}>
      <Icon className="w-3 h-3" /> {cfg.label}
    </span>
  );
}

export default function ProfileMyDataHub() {
  const { API } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadHint, setUploadHint] = useState("");
  const [guide, setGuide] = useState(false);
  const [step, setStep] = useState(0);
  const [familyOpen, setFamilyOpen] = useState(false);
  const [riskOpen, setRiskOpen] = useState(false);
  const [reproOpen, setReproOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data: res } = await axios.get(`${API}/my-data/status`);
      setData(res);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [API]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const again = () => load();
    window.addEventListener("khi:acr:saved", again);
    window.addEventListener("bhi:document:uploaded", again);
    return () => {
      window.removeEventListener("khi:acr:saved", again);
      window.removeEventListener("bhi:document:uploaded", again);
    };
  }, [load]);

  const go = (it) => {
    if (it.anchor) {
      const el = document.querySelector(`[data-testid="${it.anchor}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        try {
          el.classList.add("ring-2", "ring-cyan-400");
          setTimeout(() => el.classList.remove("ring-2", "ring-cyan-400"), 1800);
        } catch { /* noop */ }
        return;
      }
    }
    if (it.route === "profile-edit") {
      try { window.dispatchEvent(new CustomEvent("profile:edit:open")); } catch { /* noop */ }
      return;
    }
    if (it.route === "family-history") { setFamilyOpen(true); return; }
    if (it.route === "risk-score") { setRiskOpen(true); return; }
    if (it.route === "reproductive") { setReproOpen(true); return; }
    if (it.upload_hint !== undefined && !it.route) {
      setUploadHint(it.upload_hint || "");
      setUploadOpen(true);
      return;
    }
    if (it.route) navigate(it.route);
  };

  const card = isDark ? "bg-slate-900/60 border-slate-800" : "bg-white border-slate-200";
  const items = data?.items || [];
  const pending = items.filter((i) => i.status !== "complete");
  const current = step < pending.length ? pending[step] : null;

  return (
    <div className={`p-5 rounded-2xl border ${card}`} data-testid="profile-my-data-hub">
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-violet-600
                        flex items-center justify-center shrink-0">
          <Database className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <h2 className="text-lg font-extrabold">📥 Моите данни</h2>
            {data?.summary && (
              <span className="text-xs font-bold text-cyan-500" data-testid="profile-my-data-summary">
                {data.summary.complete}/{data.summary.total} · {data.summary.percent}%
              </span>
            )}
          </div>
          <p className={`text-xs mt-1 ${isDark ? "text-slate-400" : "text-slate-600"}`}>
            Всичко на едно място — попълвате данните тук веднъж и те се ползват от
            всички модули: чинията, бъбрека, гърдата, черния дроб, лабораториите и
            проверките на лекарствата.
          </p>
          <p className="text-[10px] mt-1 opacity-75" data-testid="profile-my-data-sync-note">
            <span className="font-bold text-cyan-500">Въвеждате веднъж — ползва се навсякъде:</span>{" "}
            изследванията захранват автодиагнозата и бъбречните лимити · заболяванията и
            лекарствата — проверките на чинията и взаимодействията · албуминът и филтрацията
            — стадия и риска · фамилната анамнеза — наследствения риск в гърдата и женското здраве.
          </p>
          {data?.next_step_bg && (
            <p className="text-[11px] mt-1 font-semibold text-amber-500"
              data-testid="profile-my-data-next">
              Следваща стъпка: {data.next_step_bg}
            </p>
          )}
        </div>
      </div>

      {data?.summary && (
        <div className={`mt-3 h-2 rounded-full overflow-hidden ${isDark ? "bg-white/10" : "bg-slate-200"}`}>
          <div className="h-full bg-gradient-to-r from-cyan-500 to-violet-600 transition-all"
            style={{ width: `${data.summary.percent}%` }} />
        </div>
      )}

      <button
        onClick={() => { setUploadHint(""); setUploadOpen(true); }}
        className="mt-3 w-full px-4 py-3 rounded-xl text-sm font-bold text-white
                   bg-gradient-to-r from-cyan-500 to-violet-600 hover:opacity-95
                   flex items-center justify-center gap-2 shadow-md"
        data-testid="profile-my-data-upload-any"
      >
        <FileText className="w-4 h-4" />
        Качи документ (лаборатория, ДНК, епикриза)
      </button>

      {/* iter1010 — наставник: води стъпка по стъпка, докато плочките позеленеят */}
      {!loading && pending.length > 0 && !guide && (
        <button onClick={() => { setGuide(true); setStep(0); }}
          className={`mt-2 w-full px-4 py-2.5 rounded-xl text-sm font-bold border transition-colors ${
            isDark ? "border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10"
                   : "border-cyan-500 text-cyan-700 hover:bg-cyan-50"}`}
          data-testid="profile-my-data-guide-start">
          Води ме стъпка по стъпка ({pending.length} за попълване)
        </button>
      )}

      {guide && current && (
        <div className={`mt-3 p-4 rounded-2xl border-2 ${
          isDark ? "border-cyan-500/40 bg-cyan-500/[0.07]" : "border-cyan-400 bg-cyan-50"}`}
          data-testid="profile-my-data-guide">
          <div className="flex items-center justify-between gap-2">
            <span className="text-[10px] font-bold uppercase tracking-widest text-cyan-500"
              data-testid="profile-my-data-guide-step">
              Стъпка {Math.min(step + 1, pending.length)} от {pending.length}
            </span>
            <button onClick={() => setGuide(false)}
              className="text-[11px] font-semibold opacity-60 hover:opacity-100"
              data-testid="profile-my-data-guide-exit">
              Излез
            </button>
          </div>
          <div className="text-sm font-extrabold mt-1">{current.label_bg}</div>
          <div className={`text-[11px] mt-1 ${isDark ? "text-slate-300" : "text-slate-700"}`}>
            {current.desc_bg}
          </div>
          <div className="text-[11px] mt-1 font-semibold opacity-80">{current.detail_bg}</div>
          <div className="flex flex-wrap gap-2 mt-3">
            <button onClick={() => go(current)}
              className="px-3.5 py-2 rounded-xl text-[12px] font-bold text-white
                         bg-gradient-to-r from-cyan-500 to-violet-600 hover:opacity-95"
              data-testid="profile-my-data-guide-do">
              {current.cta_bg}
            </button>
            <button onClick={async () => { await load(); setStep((s) => s + 1); }}
              className={`px-3.5 py-2 rounded-xl text-[12px] font-bold border ${
                isDark ? "border-white/15 hover:bg-white/5" : "border-slate-300 hover:bg-slate-100"}`}
              data-testid="profile-my-data-guide-next">
              Готово, напред
            </button>
            <button onClick={() => setStep((s) => s + 1)}
              className="px-3 py-2 rounded-xl text-[12px] font-semibold opacity-60 hover:opacity-100"
              data-testid="profile-my-data-guide-skip">
              Пропусни
            </button>
          </div>
        </div>
      )}

      {guide && !current && (
        <div className="mt-3 p-4 rounded-2xl border-2 border-emerald-400 bg-emerald-500/10"
          data-testid="profile-my-data-guide-done">
          <div className="text-sm font-extrabold text-emerald-600 dark:text-emerald-400">
            {pending.length ? "Минахме през всички стъпки." : "Всички плочки са зелени. Готово!"}
          </div>
          <div className="text-[11px] mt-1 opacity-80">
            {pending.length
              ? `Остават ${pending.length} за попълване — можете да се върнете по всяко време.`
              : "Данните Ви са пълни — всички модули вече работят с точните Ви стойности."}
          </div>
          <button onClick={() => { setGuide(false); setStep(0); }}
            className="mt-2 text-[12px] font-bold text-emerald-600 dark:text-emerald-400"
            data-testid="profile-my-data-guide-close">
            Затвори наставника
          </button>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin" /></div>
      ) : (
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {items.map((it) => {
            const Icon = ICONS[it.icon] || Circle;
            return (
              <div key={it.key}
                className={`p-3 rounded-xl border transition-all hover:shadow-md ${
                  isDark ? "border-white/10 hover:bg-white/5" : "border-gray-200 hover:bg-gray-50"}`}>
                <button type="button" onClick={() => go(it)} className="w-full text-left"
                  data-testid={`profile-my-data-${it.key}`}>
                  <span className="flex items-center gap-2 mb-1">
                    <Icon className={`w-4 h-4 ${it.color}`} />
                    <StatusPill status={it.status} />
                  </span>
                  <span className="block text-xs font-bold leading-snug">{it.label_bg}</span>
                  <span className={`block text-[10px] leading-snug mt-0.5 ${isDark ? "text-slate-400" : "text-slate-600"}`}>
                    {it.desc_bg}
                  </span>
                  <span className="block text-[10px] mt-1 font-semibold opacity-80"
                    data-testid={`profile-my-data-detail-${it.key}`}>
                    {it.detail_bg}
                  </span>
                  <span className="text-[10px] mt-1 inline-flex items-center gap-0.5 text-cyan-500 font-semibold">
                    {it.cta_bg} <ChevronRight className="w-3 h-3" />
                  </span>
                </button>
                {it.secondary_route && (
                  <button type="button" onClick={() => navigate(it.secondary_route)}
                    className="block text-[10px] mt-0.5 underline opacity-60 hover:opacity-100"
                    data-testid={`profile-my-data-secondary-${it.key}`}>
                    {it.secondary_cta_bg}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="text-[9px] opacity-50 mt-3">{data?.disclaimer_bg}</div>

      <FamilyHistoryModal open={familyOpen} onClose={() => setFamilyOpen(false)}
        onSaved={() => load()} />

      <RiskScoreModal open={riskOpen} onClose={() => setRiskOpen(false)}
        onSaved={() => load()} />

      <ReproductiveHistoryModal open={reproOpen} onClose={() => setReproOpen(false)}
        onSaved={() => load()} />

      <UnifiedDocumentUpload
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        lang="bg"
        initialHint={uploadHint}
        onDone={(res) => {
          load();
          try { window.dispatchEvent(new CustomEvent("bhi:document:uploaded", { detail: res })); } catch { /* noop */ }
        }}
      />
    </div>
  );
}
